import type { RouteObject } from "react-router-dom";
import { lazy, Suspense } from "react";
import NotFound from "../pages/NotFound";
import Home from "../pages/home/page";
import Login from "../pages/login/page";
import Dashboard from "../pages/dashboard/page";
import PatientRegistration from "../pages/patient-registration/page";
import VitalsEntry from "../pages/vitals-entry/page";
import ViewVitals from "../pages/view-vitals/page";
import UserManagement from "../pages/user-management/page";
import ActivityLogs from "../pages/activity-logs/page";
import EditRequests from "../pages/edit-requests/page";
import BPEntry from "../pages/bp-entry/page";
import HeartRateEntry from "../pages/heart-rate-entry/page";
import SpO2Entry from "../pages/spo2-entry/page";
import TemperatureEntry from "../pages/temperature-entry/page";
import RBSEntry from "../pages/rbs-entry/page";
import HeightWeightEntryPage from "../pages/height-weight-entry/page";

const CholesterolEntryPage = lazy(() => import('../pages/cholesterol-entry/page'));
const BankSettingsPage = lazy(() => import('../pages/accounts/bank-settings/page'));
const ExpensesPage = lazy(() => import('../pages/accounts/expenses/page'));
const CollectFeePage = lazy(() => import('../pages/accounts/collect-fee/page'));
const RevenuePage = lazy(() => import('../pages/accounts/revenue/page'));
const NetProfitPage = lazy(() => import('../pages/accounts/net-profit/page'));
const AccountsDashboardPage = lazy(() => import('../pages/accounts/dashboard/page'));
const BloodGroupingEntryPage = lazy(() => import('../pages/blood-grouping-entry/page'));
const ColourBlindnessEntryPage = lazy(() => import('../pages/colour-blindness-entry/page'));
const VisualAcuityEntryPage = lazy(() => import('../pages/visual-acuity-entry/page'));
const ECGEntryPage = lazy(() => import('../pages/ecg-entry/page'));
const PatientMonitorVitalsPage = lazy(() => import('../pages/patient-monitor-vitals/page'));
const BodyFatEntryPage = lazy(() => import('../pages/body-fat-entry/page'));
const AnalyticsPage = lazy(() => import('../pages/analytics/page'));
const EventsPage = lazy(() => import('../pages/events/page'));
const TestMasterPage = lazy(() => import('../pages/test-master/page'));
const AuditLogsPage = lazy(() => import('../pages/audit-logs/page'));
const SettingsPage = lazy(() => import('../pages/settings/page'));
const ChestCircumferenceEntryPage = lazy(() => import('../pages/chest-circumference-entry/page'));
const RespiratoryEntryPage = lazy(() => import('../pages/respiratory-entry/page'));

const Fallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const routes: RouteObject[] = [
  { path: "/", element: <Home /> },
  { path: "/login", element: <Login /> },
  { path: "/dashboard", element: <Dashboard /> },
  { path: "/patient-registration", element: <PatientRegistration /> },
  { path: "/vitals-entry", element: <VitalsEntry /> },
  { path: "/view-vitals", element: <ViewVitals /> },
  { path: "/user-management", element: <UserManagement /> },
  { path: "/activity-logs", element: <ActivityLogs /> },
  { path: "/edit-requests", element: <EditRequests /> },
  { path: "/bp-entry", element: <BPEntry /> },
  { path: "/heart-rate-entry", element: <HeartRateEntry /> },
  { path: "/spo2-entry", element: <SpO2Entry /> },
  { path: "/temperature-entry", element: <TemperatureEntry /> },
  { path: "/rbs-entry", element: <RBSEntry /> },
  {
    path: '/cholesterol-entry',
    element: <Suspense fallback={<Fallback />}><CholesterolEntryPage /></Suspense>,
  },
  {
    path: '/height-weight-entry',
    element: <HeightWeightEntryPage />,
  },
  {
    path: '/blood-grouping-entry',
    element: <Suspense fallback={<Fallback />}><BloodGroupingEntryPage /></Suspense>,
  },
  {
    path: '/colour-blindness-entry',
    element: <Suspense fallback={<Fallback />}><ColourBlindnessEntryPage /></Suspense>,
  },
  {
    path: '/visual-acuity-entry',
    element: <Suspense fallback={<Fallback />}><VisualAcuityEntryPage /></Suspense>,
  },
  {
    path: '/ecg-entry',
    element: <Suspense fallback={<Fallback />}><ECGEntryPage /></Suspense>,
  },
  {
    path: '/accounts/dashboard',
    element: <Suspense fallback={<Fallback />}><AccountsDashboardPage /></Suspense>,
  },
  {
    path: '/accounts/bank-settings',
    element: <Suspense fallback={<Fallback />}><BankSettingsPage /></Suspense>,
  },
  {
    path: '/accounts/expenses',
    element: <Suspense fallback={<Fallback />}><ExpensesPage /></Suspense>,
  },
  {
    path: '/accounts/collect-fee',
    element: <Suspense fallback={<Fallback />}><CollectFeePage /></Suspense>,
  },
  {
    path: '/accounts/revenue',
    element: <Suspense fallback={<Fallback />}><RevenuePage /></Suspense>,
  },
  {
    path: '/accounts/net-profit',
    element: <Suspense fallback={<Fallback />}><NetProfitPage /></Suspense>,
  },
  {
    path: '/patient-monitor-vitals',
    element: <Suspense fallback={<Fallback />}><PatientMonitorVitalsPage /></Suspense>,
  },
  {
    path: '/body-fat-entry',
    element: <Suspense fallback={<Fallback />}><BodyFatEntryPage /></Suspense>,
  },
  {
    path: '/chest-circumference-entry',
    element: <Suspense fallback={<Fallback />}><ChestCircumferenceEntryPage /></Suspense>,
  },
  {
    path: '/respiratory-entry',
    element: <Suspense fallback={<Fallback />}><RespiratoryEntryPage /></Suspense>,
  },
  {
    path: '/test-master',
    element: <Suspense fallback={<Fallback />}><TestMasterPage /></Suspense>,
  },
  {
    path: '/audit-logs',
    element: <Suspense fallback={<Fallback />}><AuditLogsPage /></Suspense>,
  },
  {
    path: '/settings',
    element: <Suspense fallback={<Fallback />}><SettingsPage /></Suspense>,
  },
  {
    path: '/analytics',
    element: <Suspense fallback={<Fallback />}><AnalyticsPage /></Suspense>,
  },
  {
    path: '/events',
    element: <Suspense fallback={<Fallback />}><EventsPage /></Suspense>,
  },
  { path: "*", element: <NotFound /> },
];

export default routes;
